import type { CustomBlockRenderRepresentationType } from '@readme/api/src/mappings/customblock/types';

import debounce from 'lodash/debounce';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import type { ConfigContextValue, ProjectContextValue } from '@core/context';
import { ConfigContext, ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useLegacyCompatibleBranchName from '@core/hooks/useLegacyCompatibleBranchName';
import useLocalStorage from '@core/hooks/useLocalStorage';
import { fetcher } from '@core/hooks/useReadmeApi';
import type { HTTPError } from '@core/utils/types/errors';

import Badge from '@ui/Badge';
import Button from '@ui/Button';
import CodeInput from '@ui/CodeInput';
import Dropdown from '@ui/Dropdown';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Menu, { MenuHeader, MenuItem } from '@ui/Menu';
import RDMD from '@ui/RDMD';
import ErrorBoundary from '@ui/RDMD/ErrorBoundary';
import { RHFGroup } from '@ui/RHF';
import Tooltip from '@ui/Tooltip';

import { useCustomBlockFormContext } from '../Context';

import { newStateExample } from './newStateExample';
import classes from './style.module.scss';

const VALIDATION_TIMEOUT_MS: number =
  (process.env.MDX_CHILD_PROCESS_TIMEOUT_MS && parseInt(process.env.MDX_CHILD_PROCESS_TIMEOUT_MS, 10)) || 3000;

export default function ComponentEditor() {
  const bem = useClassy(classes, 'ComponentEditor');

  const { name: dashOrHub } = useContext(ConfigContext) as ConfigContextValue;
  const themeStorageKey = `${dashOrHub}:ComponentEditor-theme`;
  const layoutStorageKey = `${dashOrHub}:ComponentEditor-layout`;

  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const version = useLegacyCompatibleBranchName();
  const { control } = useCustomBlockFormContext();
  const sourceValue = useWatch({ control, name: 'source' });
  const nameValue = useWatch({ control, name: 'name' });
  const isNewComponent = window.location.href.match(/custom-components\/new/) !== null;
  const storage = useLocalStorage();

  // Check if there is marketplace data (name and source are pre-populated)
  const hasMarketplaceData = isNewComponent && nameValue && sourceValue && sourceValue !== newStateExample;

  const [currentField, setCurrentField] = useState(() => {
    if (hasMarketplaceData) {
      return sourceValue;
    }
    return isNewComponent ? newStateExample : sourceValue;
  });

  const [dehydrated, setDehydrated] = useState<string | null>(null);
  const [layout, setLayout] = useState(storage.getItem(layoutStorageKey) || 'col');
  const [error, setError] = useState<HTTPError<{ detail: string }> | null>(null);

  // The component editor appears over a dark UI in the Hub and a light UI in the Enterprise Dash.
  const defaultColorMode = dashOrHub === 'Hub' ? 'dark' : 'light';
  const [theme, setTheme] = useState(storage.getItem(themeStorageKey) || defaultColorMode);

  // locally store layout and theme preferences
  useEffect(() => {
    storage.setItem(layoutStorageKey, layout);
    storage.setItem(themeStorageKey, theme);
  }, [layoutStorageKey, themeStorageKey, layout, storage, theme]);

  // Update currentField when marketplace data becomes available
  useEffect(() => {
    if (hasMarketplaceData && sourceValue !== currentField) {
      setCurrentField(sourceValue);
    }
  }, [hasMarketplaceData, sourceValue, currentField]);

  const toggleLayout = () => {
    setLayout((prevLayout: string) => (prevLayout === 'row' ? 'col' : 'row'));
  };

  const toggleTheme = () => {
    setTheme((prevTheme: string) => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  const onFieldChange = (value: string) => {
    setCurrentField(value);
  };

  const debouncedValidation = useMemo(() => {
    return debounce(async (source, self) => {
      setError(null);

      if (!source) {
        return;
      }

      try {
        const tag = 'new';
        const response = await fetcher<CustomBlockRenderRepresentationType>(
          `/${project.subdomain}/api-next/v2/versions/${version}/custom_blocks/${tag}/render`,
          {
            body: JSON.stringify({ type: 'component', name: tag, source }),
            method: 'POST',
          },
        );

        setError(null);
        setDehydrated(response.data.dehydrated);
      } catch (e) {
        setError(e);
        setDehydrated(null);
      } finally {
        self.flush();
      }
    }, VALIDATION_TIMEOUT_MS);
  }, [project.subdomain, version]);

  useEffect(() => {
    debouncedValidation(currentField, debouncedValidation);

    return debouncedValidation.cancel;
  }, [debouncedValidation, debouncedValidation.cancel, currentField]);

  return (
    <Flex className={bem('&')} data-testid="ComponentEditor" gap={0} layout={layout}>
      <div className={bem('-inputWrapper')} data-label="index.mdx">
        {/* @note: 250000 is coming from the mdx-renderer: https://github.com/readmeio/mdx-renderer/commit/d8e81faadef3f1a32ca804abb04c3981585ed851#diff-8a8ae07582c9d433ec8c2e5c4310ff8901e604f4965c5b90a49117ad46c47595R65 */}
        <RHFGroup control={control} errorStyle="tooltip" maxLength={250000} name="source" required>
          {/*
           * @note: If you don't destructure `onChange` off of `field`,
           * somehow when it's expanded onto the props, it causes what appears
           * to be some re-rendering issues? 🤷
           */}
          {({ field: { onChange, ...field } }) => (
            <CodeInput
              {...field}
              className={bem('-codeInput')}
              initialValue={currentField}
              language="jsx"
              onChange={e => {
                onChange(e);
                onFieldChange(e);
              }}
              size="sm"
            />
          )}
        </RHFGroup>
      </div>
      <div className={bem('-preview', `-preview_${layout}`, `-preview_${theme}`)} data-color-mode={theme}>
        <Flex align="center" className={bem('-preview-header')} justify="between">
          <Flex align="center">
            <Badge allCaps circular={false} kind={theme}>
              Preview
            </Badge>
          </Flex>
          <Flex align="center" className={bem('-preview-header-controls')} gap={0}>
            <Tooltip asTitle content={theme === 'dark' ? 'Set Light Mode' : 'Set Dark Mode'} placement="bottom-end">
              <Button ghost kind="contrast" onClick={toggleTheme} size="sm">
                <Icon name={theme === 'dark' ? 'moon' : 'sun'} />
              </Button>
            </Tooltip>
            <Tooltip asTitle content={layout === 'row' ? 'Split Horizontal' : 'Split Vertical'} placement="bottom-end">
              <Button ghost kind="contrast" onClick={toggleLayout} size="sm">
                <Icon name={layout === 'row' ? 'columns-2' : 'rows-2'} />
              </Button>
            </Tooltip>
            <Tooltip asTitle content="Marketplace" placement="bottom-end">
              <Button
                ghost
                href="https://github.com/readmeio/marketplace"
                kind="contrast"
                rel="noreferrer"
                size="sm"
                target="_blank"
              >
                <Icon aria-label="Opens in a new tab" name="marketplace" />
              </Button>
            </Tooltip>
            <Dropdown sticky>
              <Button ghost kind="contrast" size="sm">
                <Icon name="help-circle" />
              </Button>
              <Menu>
                <MenuHeader>Resources</MenuHeader>
                <MenuItem
                  href="https://docs.readme.com/main/v3.0/docs/building-custom-mdx-components"
                  TagName="a"
                  target="_blank"
                >
                  <span>Building Components</span>
                </MenuItem>
                <MenuItem href="https://tailwindcss.com/docs/styling-with-utility-classes" TagName="a" target="_blank">
                  <span>Styling with Tailwind</span>
                </MenuItem>
              </Menu>
            </Dropdown>
          </Flex>
        </Flex>
        <Flex className={bem('-preview-render')} data-theme={theme}>
          <ErrorBoundary apiError={error} body={currentField}>
            {!!dehydrated && (
              <RDMD
                body={currentField}
                darkModeDataAttribute="data-theme"
                dehydrated={dehydrated}
                format="mdx"
                opts={{ useTailwindRoot: true }}
              />
            )}
          </ErrorBoundary>
        </Flex>
      </div>
    </Flex>
  );
}
